import ApiService from './apiService';

/**
 * Service pour gérer les permissions des utilisateurs
 */
class PermissionsService {
    /**
     * Récupérer la liste de toutes les permissions disponibles
     * @returns {Promise} Réponse de l'API avec la liste des permissions
     */
    getAvailablePermissions() {
        return ApiService.get('/user-permissions');
    }

    /**
     * Récupérer les permissions de l'utilisateur actuellement authentifié
     * @returns {Promise} Réponse de l'API avec les permissions de l'utilisateur actuel
     */
    getCurrentUserPermissions() {
        return ApiService.get('/user-permissions/current');
    }

    /**
     * Récupérer les permissions d'un utilisateur spécifique
     * @param {Number} userId ID de l'utilisateur
     * @returns {Promise} Réponse de l'API avec les permissions de l'utilisateur
     * @throws {Error} Si l'utilisateur n'est pas admin ou si l'utilisateur n'existe pas
     */
    getUserPermissions(userId) {
        return ApiService.get(`/user-permissions/user/${userId}`);
    }

    /**
     * Synchroniser (remplacer) toutes les permissions d'un utilisateur
     * @param {Number} userId ID de l'utilisateur
     * @param {Array<String>} permissions Liste des permissions à définir
     * @returns {Promise} Réponse de l'API avec les permissions mises à jour
     * @throws {Error} Si l'utilisateur n'est pas admin, si l'utilisateur n'existe pas, ou si on tente de modifier ses propres permissions
     */
    syncUserPermissions(userId, permissions) {
        return ApiService.post(`/user-permissions/user/${userId}/sync`, {
            permissions
        });
    }

    /**
     * Ajouter des permissions à un utilisateur
     * @param {Number} userId ID de l'utilisateur
     * @param {Array<String>} permissions Liste des permissions à ajouter
     * @returns {Promise} Réponse de l'API avec les permissions mises à jour
     * @throws {Error} Si l'utilisateur n'est pas admin, si l'utilisateur n'existe pas, ou si on tente de modifier ses propres permissions
     */
    addUserPermissions(userId, permissions) {
        return ApiService.post(`/user-permissions/user/${userId}/add`, {
            permissions
        });
    }

    /**
     * Retirer des permissions d'un utilisateur
     * @param {Number} userId ID de l'utilisateur
     * @param {Array<String>} permissions Liste des permissions à retirer
     * @returns {Promise} Réponse de l'API avec les permissions mises à jour
     * @throws {Error} Si l'utilisateur n'est pas admin, si l'utilisateur n'existe pas, ou si on tente de modifier ses propres permissions
     */
    removeUserPermissions(userId, permissions) {
        return ApiService.post(`/user-permissions/user/${userId}/remove`, {
            permissions
        });
    }

    /**
     * Supprimer toutes les permissions d'un utilisateur
     * @param {Number} userId ID de l'utilisateur
     * @returns {Promise} Réponse de l'API confirmant la suppression
     * @throws {Error} Si l'utilisateur n'est pas admin, si l'utilisateur n'existe pas, ou si on tente de modifier ses propres permissions
     */
    clearUserPermissions(userId) {
        return ApiService.delete(`/user-permissions/user/${userId}/clear`);
    }
}

export default new PermissionsService();
